import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { StatusChip } from '@/components/ui/status-chip';
import { formatBrokerDateTime } from '@/features/brokers/batches/batch-formatters';
import { useBrokerClients } from '@/hooks/queries/brokers/use-broker-detail';
import type { WireClientAccount } from '@/services/api/brokers';
import { CHIP_XS_CLASS } from './broker-detail-format';
import { ErrorPanel, LoadMoreButton } from './detail-panels';

// Module-level columns — a per-render array would reset DataTable state each render.
const COLUMNS: ColumnDef<WireClientAccount, unknown>[] = [
  {
    accessorKey: 'client_account_id',
    header: 'Client Account ID',
    cell: ({ row }) => (
      // UUID id-space; the v2 user console route is pending but the link is emitted
      // anyway for parity with the compliance-alerts table
      <Link className="font-mono text-xs text-blue-500 hover:underline" onClick={(event) => event.stopPropagation()} to={`/operations/users/${row.original.client_account_id}`}>
        {row.original.client_account_id}
      </Link>
    ),
  },
  { accessorKey: 'fast_id', header: 'FAST ID', cell: ({ row }) => <span className="font-mono text-xs">{row.original.fast_id}</span> },
  { accessorKey: 'broker_reference', header: 'Broker Reference', cell: ({ row }) => row.original.broker_reference || '-' },
  {
    accessorKey: 'product_eligibility',
    header: 'Product Eligibility',
    enableSorting: false,
    cell: ({ row }) => {
      const eligibility = row.original.product_eligibility;
      if (!eligibility || eligibility.length === 0) return '-';
      return (
        <span className="flex flex-wrap gap-1">
          {eligibility.map((product) => (
            <StatusChip className={CHIP_XS_CLASS} key={product} variant="info">
              {product}
            </StatusChip>
          ))}
        </span>
      );
    },
  },
  { accessorKey: 'created_at', header: 'Created', cell: ({ row }) => <DateCell format={formatBrokerDateTime} value={row.original.created_at} /> },
];

interface ClientsTabProps {
  brokerId: string;
}

export const ClientsTab = ({ brokerId }: ClientsTabProps) => {
  const query = useBrokerClients(brokerId);

  // Full panel only when nothing loaded; a failed Load More keeps the table
  // mounted with its rows and surfaces the error inline below.
  if (query.isError && !query.data) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch clients" />;

  return (
    <div className="flex flex-col gap-3">
      {/* isPending (not isFetching): the table stays mounted with data during Load More */}
      <DataTable columns={COLUMNS} data={query.data} emptyMessage="No clients found." getRowId={(row) => row.client_account_id} isLoading={query.isPending} />
      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch clients" />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
ClientsTab.displayName = 'ClientsTab';
